import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Plus, X, Save, Link2, Unlink, CalendarDays, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import axios from 'axios';
import Logo from './Logo';
import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const BusinessSetup = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [receptionistName, setReceptionistName] = useState('Mia');
  const [greetingMessage, setGreetingMessage] = useState('Thank you for calling. How can I help you today?');
  const [businessHours, setBusinessHours] = useState('');
  const [tzone, setTzone] = useState('America/New_York');
  const [services, setServices] = useState(['']);
  const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);
  const [messageQuestions, setMessageQuestions] = useState(['']);
  
  // Scale plan features
  const [planType, setPlanType] = useState('professional');
  const [appointmentBooking, setAppointmentBooking] = useState(false);
  const [calendarIntegration, setCalendarIntegration] = useState('');
  const [calendarLink, setCalendarLink] = useState('');
  const [bookingLinkInput, setBookingLinkInput] = useState('');
  const [savingBookingLink, setSavingBookingLink] = useState(false);
  const [callTransfer, setCallTransfer] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [warmTransfer, setWarmTransfer] = useState(false);
  const [smsDuringCalls, setSmsDuringCalls] = useState(false);

  // Calendly integration
  const [calendlyConnected, setCalendlyConnected] = useState(false);
  const [calendlyInfo, setCalendlyInfo] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState('');
  const [loadingCalendly, setLoadingCalendly] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const response = await axios.get(`${API}/business/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = response.data;
        
        setHasProfile(true);
        setBusinessName(profile.business_name || '');
        setBusinessDescription(profile.business_description || '');
        setReceptionistName(profile.receptionist_name || 'Mia');
        setGreetingMessage(profile.greeting_message || '');
        setBusinessHours(profile.business_hours || '');
        setTzone(profile.timezone || 'America/New_York');
        setServices(profile.services.length > 0 ? profile.services : ['']);
        setFaqs(profile.faqs.length > 0 ? profile.faqs : [{ question: '', answer: '' }]);
        setMessageQuestions(profile.message_taking_questions?.length > 0 ? profile.message_taking_questions : ['']);
        
        // Scale plan features
        setPlanType(profile.plan_type || 'professional');
        setAppointmentBooking(profile.appointment_booking_enabled || false);
        setCalendarIntegration(profile.calendar_integration || '');
        setCalendarLink(profile.calendar_link || '');
        setBookingLinkInput(profile.calendar_link || '');
        setCallTransfer(profile.call_transfer_enabled || false);
        setTransferNumber(profile.transfer_phone_number || '');
        setWarmTransfer(profile.warm_transfer_enabled || false);
        setSmsDuringCalls(profile.sms_during_calls_enabled || false);
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error fetching profile:', error);
        }
      }
    };

    fetchBusinessProfile();
    fetchCalendlyStatus();

    // Handle Calendly OAuth callback
    const calendlyParam = searchParams.get('calendly');
    if (calendlyParam === 'success') {
      toast({ title: 'Calendly connected successfully!' });
      fetchCalendlyStatus();
    } else if (calendlyParam === 'error') {
      toast({ title: 'Failed to connect Calendly', variant: 'destructive' });
    }
  }, [token]);

  const fetchCalendlyStatus = async () => {
    try {
      const res = await axios.get(`${API}/calendly/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.connected) {
        setCalendlyConnected(true);
        setCalendlyInfo(res.data);
        fetchEventTypes();
      }
    } catch (err) {
      console.log('Calendly not connected');
    }
  };

  const fetchEventTypes = async () => {
    try {
      const res = await axios.get(`${API}/calendly/event-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventTypes(res.data.event_types || []);
    } catch (err) {
      console.log('Error fetching event types:', err);
    }
  };

  const connectCalendly = () => {
    // Get user_id from token
    const payload = JSON.parse(atob(token.split('.')[1]));
    const userId = payload.user_id;
    window.location.href = `${API}/calendly/connect?user_id=${userId}&origin=${encodeURIComponent(window.location.origin)}`;
  };

  const bookingProviderName = (url) => {
    if (!url) return '';
    if (url.includes('calendar.app.google') || url.includes('calendar.google.com')) return 'Google Calendar';
    if (url.includes('cal.com')) return 'Cal.com';
    if (url.includes('calendly.com')) return 'Calendly';
    return 'Booking link';
  };

  const saveBookingLink = async () => {
    if (!bookingLinkInput.trim()) return;
    setSavingBookingLink(true);
    try {
      const res = await axios.post(`${API}/calendly/set-link`, { calendly_url: bookingLinkInput.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarLink(res.data.calendar_link);
      toast({ title: `${bookingProviderName(res.data.calendar_link)} booking link saved` });
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Invalid booking link', variant: 'destructive' });
    } finally {
      setSavingBookingLink(false);
    }
  };

  const disconnectCalendly = async () => {
    setLoadingCalendly(true);
    try {
      await axios.delete(`${API}/calendly/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendlyConnected(false);
      setCalendlyInfo(null);
      setEventTypes([]);
      setCalendarLink('');
      toast({ title: 'Calendly disconnected' });
    } catch (err) {
      toast({ title: 'Error disconnecting', variant: 'destructive' });
    } finally {
      setLoadingCalendly(false);
    }
  };

  const selectEventType = async (eventTypeUri) => {
    const et = eventTypes.find(e => e.uri === eventTypeUri);
    if (!et) return;
    setLoadingCalendly(true);
    try {
      await axios.post(`${API}/calendly/select-event-type`, {
        event_type_uri: et.uri,
        event_type_name: et.name,
        scheduling_url: et.scheduling_url
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedEventType(eventTypeUri);
      setCalendarLink(et.scheduling_url);
      setAppointmentBooking(true);
      toast({ title: `Event type "${et.name}" selected for AI booking` });
    } catch (err) {
      toast({ title: 'Error selecting event type', variant: 'destructive' });
    } finally {
      setLoadingCalendly(false);
    }
  };

  const handleSave = async () => {
    if (!businessName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Business name is required',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const profileData = {
        business_name: businessName,
        business_description: businessDescription,
        receptionist_name: receptionistName,
        greeting_message: greetingMessage,
        business_hours: businessHours,
        timezone: tzone,
        services: services.filter(s => s.trim() !== ''),
        faqs: faqs.filter(f => f.question.trim() !== '' && f.answer.trim() !== ''),
        message_taking_questions: messageQuestions.filter(q => q.trim() !== ''),
        language_support: 'bilingual',
        
        // Scale plan features
        plan_type: planType,
        appointment_booking_enabled: appointmentBooking,
        calendar_integration: calendarIntegration,
        calendar_link: calendarLink,
        call_transfer_enabled: callTransfer,
        transfer_phone_number: transferNumber,
        warm_transfer_enabled: warmTransfer,
        sms_during_calls_enabled: smsDuringCalls
      };

      if (hasProfile) {
        await axios.put(`${API}/business/profile`, profileData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: 'Success!',
          description: 'Business profile updated successfully'
        });
      } else {
        await axios.post(`${API}/business/profile`, profileData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: 'Success!',
          description: 'Business profile created successfully'
        });
        setHasProfile(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save profile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const addService = () => setServices([...services, '']);
  const removeService = (index) => setServices(services.filter((_, i) => i !== index));
  const updateService = (index, value) => {
    const newServices = [...services];
    newServices[index] = value;
    setServices(newServices);
  };

  const addFaq = () => setFaqs([...faqs, { question: '', answer: '' }]);
  const removeFaq = (index) => setFaqs(faqs.filter((_, i) => i !== index));
  const updateFaq = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const addMessageQuestion = () => setMessageQuestions([...messageQuestions, '']);
  const removeMessageQuestion = (index) => setMessageQuestions(messageQuestions.filter((_, i) => i !== index));
  const updateMessageQuestion = (index, value) => {
    const newQuestions = [...messageQuestions];
    newQuestions[index] = value;
    setMessageQuestions(newQuestions);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            <h1 className="text-xl font-bold text-white">Business Setup</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-gradient-to-br from-teal-900/20 to-purple-900/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Basic Information</CardTitle>
              <CardDescription className="text-gray-400">
                Tell us about your business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName" className="text-gray-200">
                  Business Name *
                </Label>
                <Input
                  id="businessName"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Your Business Name"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessDescription" className="text-gray-200">
                  Business Description
                </Label>
                <Textarea
                  id="businessDescription"
                  value={businessDescription}
                  onChange={(e) => setBusinessDescription(e.target.value)}
                  placeholder="Describe what your business does..."
                  rows={4}
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessHours" className="text-gray-200">
                  Business Hours
                </Label>
                <Input
                  id="businessHours"
                  value={businessHours}
                  onChange={(e) => setBusinessHours(e.target.value)}
                  placeholder="e.g., Monday-Friday 9 AM - 5 PM"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500">Mia answers calls 24/7 — these hours control when calls can be transferred to your team.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tzSelect" className="text-gray-200">Timezone</Label>
                <select
                  id="tzSelect"
                  data-testid="business-timezone-select"
                  value={tzone}
                  onChange={(e) => setTzone(e.target.value)}
                  className="w-full h-10 rounded-md px-3 bg-black/30 border border-white/10 text-white text-sm"
                >
                  <option value="America/New_York">Eastern (New York)</option>
                  <option value="America/Chicago">Central (Chicago)</option>
                  <option value="America/Denver">Mountain (Denver)</option>
                  <option value="America/Phoenix">Arizona (Phoenix)</option>
                  <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                  <option value="America/Anchorage">Alaska</option>
                  <option value="Pacific/Honolulu">Hawaii</option>
                  <option value="Europe/London">London</option>
                  <option value="Europe/Paris">Paris / Berlin</option>
                  <option value="Australia/Sydney">Sydney</option>
                </select>
                <p className="text-xs text-gray-500">Used to know when your business is open for call transfers</p>
              </div>
            </CardContent>
          </Card>

          {/* Greeting Message */}
          <Card className="bg-gradient-to-br from-purple-900/20 to-teal-900/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">AI Receptionist Settings</CardTitle>
              <CardDescription className="text-gray-400">
                Customize your AI receptionist's name and greeting
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="receptionistName" className="text-gray-200">
                  Receptionist Name
                </Label>
                <Input
                  id="receptionistName"
                  value={receptionistName}
                  onChange={(e) => setReceptionistName(e.target.value)}
                  placeholder="Mia"
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-400">
                  Default is "Mia". Your AI receptionist will identify with this name when answering calls.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="greetingMessage" className="text-gray-200">
                  Greeting Message
                </Label>
                <Textarea
                  id="greetingMessage"
                  value={greetingMessage}
                  onChange={(e) => setGreetingMessage(e.target.value)}
                  placeholder="Thank you for calling..."
                  rows={3}
                  className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card className="bg-gradient-to-br from-teal-900/20 to-purple-900/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Services Offered</CardTitle>
              <CardDescription className="text-gray-400">
                What services does your business provide?
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {services.map((service, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={service}
                    onChange={(e) => updateService(index, e.target.value)}
                    placeholder={`Service ${index + 1}`}
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                  />
                  {services.length > 1 && (
                    <Button
                      onClick={() => removeService(index)}
                      variant="outline"
                      className="bg-transparent border-white/10 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                onClick={addService}
                variant="outline"
                className="w-full bg-transparent border-white/10 text-white hover:bg-white/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            </CardContent>
          </Card>

          {/* FAQs */}
          <Card className="bg-gradient-to-br from-purple-900/20 to-teal-900/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Frequently Asked Questions</CardTitle>
              <CardDescription className="text-gray-400">
                Common questions callers might ask
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="space-y-2 p-4 bg-black/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-200">FAQ {index + 1}</Label>
                    {faqs.length > 1 && (
                      <Button
                        onClick={() => removeFaq(index)}
                        variant="outline"
                        size="sm"
                        className="bg-transparent border-white/10 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <Input
                    value={faq.question}
                    onChange={(e) => updateFaq(index, 'question', e.target.value)}
                    placeholder="Question"
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                  />
                  <Textarea
                    value={faq.answer}
                    onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                    placeholder="Answer"
                    rows={3}
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              ))}
              <Button
                onClick={addFaq}
                variant="outline"
                className="w-full bg-transparent border-white/10 text-white hover:bg-white/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </CardContent>
          </Card>

          {/* Message Taking Questions */}
          <Card className="bg-gradient-to-br from-teal-900/20 to-purple-900/20 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Message Taking Questions</CardTitle>
              <CardDescription className="text-gray-400">
                Custom questions to ask when taking messages (Professional feature)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {messageQuestions.map((question, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={question}
                    onChange={(e) => updateMessageQuestion(index, e.target.value)}
                    placeholder={`Question ${index + 1} (e.g., "What is the best time to reach you?")`}
                    className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                  />
                  {messageQuestions.length > 1 && (
                    <Button
                      onClick={() => removeMessageQuestion(index)}
                      variant="outline"
                      className="bg-transparent border-white/10 text-red-400 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                onClick={addMessageQuestion}
                variant="outline"
                className="w-full bg-transparent border-white/10 text-white hover:bg-white/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Message Question
              </Button>
              <div className="mt-4 p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <p className="text-sm text-purple-300">
                  💡 These questions will be asked by {receptionistName || 'Mia'} when taking messages to gather specific information you need.
                </p>
              </div>

          {/* Advanced Features */}
          <Card className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border-purple-500/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Advanced Features</CardTitle>
                  <CardDescription className="text-gray-400">
                    Call transfers, scheduling, and more
                  </CardDescription>
                </div>
                <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Premium
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Plan Type Selector */}
              <div className="space-y-2">
                <Label htmlFor="planType" className="text-gray-200">
                  Current Plan
                </Label>
                <Select value={planType} onValueChange={setPlanType}>
                  <SelectTrigger className="bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10">
                    <SelectItem value="professional" className="text-white">Professional</SelectItem>
                    <SelectItem value="scale" className="text-white">Scale</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Call Transfers — Available for ALL plans */}
              <div className="p-4 bg-black/20 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white font-medium">Call Transfers</Label>
                    <p className="text-sm text-gray-400">Forward urgent calls to a team member (with hold music)</p>
                  </div>
                  <Switch
                    checked={callTransfer}
                    onCheckedChange={setCallTransfer}
                  />
                </div>
                
                {callTransfer && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-gray-200">Transfer Phone Number</Label>
                      <Input
                        value={transferNumber}
                        onChange={(e) => setTransferNumber(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="bg-black/30 border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-gray-200">Warm Transfer</Label>
                        <p className="text-sm text-gray-400">Announce caller before transferring</p>
                      </div>
                      <Switch
                        checked={warmTransfer}
                        onCheckedChange={setWarmTransfer}
                      />
                    </div>
                  </>
                )}
              </div>

              {planType === 'scale' && (
                <>
                  {/* Appointment Booking — Calendly Integration */}
                  <div className="p-4 bg-black/20 rounded-lg space-y-4" data-testid="calendly-section">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-white font-medium">Appointment Booking</Label>
                        <p className="text-sm text-gray-400">Connect Calendly or Google Calendar for automated scheduling</p>
                      </div>
                      <Switch
                        checked={appointmentBooking}
                        onCheckedChange={setAppointmentBooking}
                      />
                    </div>
                    
                    {appointmentBooking && (
                      <div className="space-y-4">
                        {!calendlyConnected ? (
                          <div className="space-y-4">
                            <Button
                              type="button"
                              onClick={connectCalendly}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid="connect-calendly-btn"
                            >
                              <Link2 className="w-4 h-4 mr-2" />
                              Connect Calendly Account
                            </Button>

                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-px bg-white/10" />
                              <span className="text-xs text-gray-500">or paste a booking link</span>
                              <div className="flex-1 h-px bg-white/10" />
                            </div>

                            <div className="space-y-2" data-testid="booking-link-section">
                              <Label className="text-gray-200 text-sm flex items-center gap-2">
                                <CalendarDays className="w-4 h-4 text-emerald-400" />
                                Google Calendar / Calendly / Cal.com link
                              </Label>
                              <div className="flex gap-2">
                                <Input
                                  value={bookingLinkInput}
                                  onChange={e => setBookingLinkInput(e.target.value)}
                                  placeholder="https://calendar.app.google/... or https://calendly.com/..."
                                  className="bg-black/30 border-white/10 text-white flex-1"
                                  data-testid="booking-link-input"
                                />
                                <Button
                                  type="button"
                                  onClick={saveBookingLink}
                                  disabled={savingBookingLink || !bookingLinkInput.trim()}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid="save-booking-link-btn"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              </div>
                              <p className="text-xs text-gray-500">
                                Google Calendar: create an appointment schedule in Google Calendar, then share its booking page link here. Mia will send it to callers and chat visitors.
                              </p>
                              {calendarLink && (
                                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-testid="saved-booking-link">
                                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 flex-shrink-0">{bookingProviderName(calendarLink)}</span>
                                  <p className="text-emerald-300 text-xs truncate">{calendarLink}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Connected status */}
                            <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-emerald-400" />
                                <div>
                                  <p className="text-emerald-300 text-sm font-medium" data-testid="calendly-connected-name">
                                    Connected: {calendlyInfo?.calendly_name}
                                  </p>
                                  <p className="text-gray-500 text-xs">{calendlyInfo?.calendly_email}</p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                onClick={disconnectCalendly}
                                disabled={loadingCalendly}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                                data-testid="disconnect-calendly-btn"
                              >
                                <Unlink className="w-3 h-3 mr-1" /> Disconnect
                              </Button>
                            </div>

                            {/* Event type selection */}
                            {eventTypes.length > 0 ? (
                              <div className="space-y-2">
                                <Label className="text-gray-200 text-sm">Select Event Type for AI Booking</Label>
                                <div className="space-y-2">
                                  {eventTypes.map((et) => (
                                    <button
                                      type="button"
                                      key={et.uri}
                                      onClick={() => selectEventType(et.uri)}
                                      data-testid={`event-type-${et.name}`}
                                      className={`w-full text-left p-3 rounded-lg border transition-all flex items-center justify-between ${
                                        selectedEventType === et.uri || calendarLink === et.scheduling_url
                                          ? 'bg-purple-500/10 border-purple-500/30 text-white'
                                          : 'bg-black/20 border-white/10 text-gray-300 hover:border-white/20'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <CalendarDays className="w-4 h-4 text-gray-500" />
                                        <div>
                                          <p className="font-medium text-sm">{et.name}</p>
                                          <p className="text-xs text-gray-500">{et.duration} min</p>
                                        </div>
                                      </div>
                                      {(selectedEventType === et.uri || calendarLink === et.scheduling_url) && (
                                        <Check className="w-4 h-4 text-purple-400" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500">No active event types found in your Calendly account.</p>
                            )}

                            {/* Show the scheduling link */}
                            {calendarLink && (
                              <div className="space-y-1">
                                <Label className="text-gray-400 text-xs">Scheduling Link (used by Mia during calls)</Label>
                                <Input
                                  value={calendarLink}
                                  readOnly
                                  className="bg-black/30 border-white/10 text-gray-300 text-sm"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* SMS During Calls */}
                  <div className="p-4 bg-black/20 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-white font-medium">Send SMS During Calls</Label>
                        <p className="text-sm text-gray-400">Send links, pricing, and confirmations via text</p>
                      </div>
                      <Switch
                        checked={smsDuringCalls}
                        onCheckedChange={setSmsDuringCalls}
                      />
                    </div>
                  </div>
                </>
              )}

              {planType === 'professional' && (
                <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg text-center">
                  <p className="text-purple-300 mb-2">Upgrade to Scale Plan</p>
                  <p className="text-sm text-gray-400">
                    Get appointment booking, Calendly integration, and SMS during calls
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default BusinessSetup;
